import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { UserProfile, loadUserProfile } from '../core/profile';
import { syncProfileToCloud } from '../core/cloudSync';
import {
  Trophy,
  Crown,
  Medal,
  Award,
  Flame,
  Shield,
  Zap,
  Sparkles,
  Cloud,
  Check,
  TrendingUp,
  GraduationCap,
  Users,
  Target,
  Swords,
  Scroll,
  ChevronRight,
  Info,
  ExternalLink,
  Search,
} from 'lucide-react';

interface SanjiePlayer {
  rank: number;
  name: string;
  avatar: string;
  affiliation: string;
  realm: 'tian' | 'di' | 'ren';
  title: string; // e.g. '不朽传说', '过A仙班', '天王座', '宗师座'
  merit: number; // 甲案官方功勋
  rating: number; // 乙案问鼎 Rating (Elo)
  winRate: string;
  bombCount: number;
  trophies: string[]; // 荣誉奖杯
  shortcutBadge?: string; // 捷径标识
  trend: 'up' | 'down' | 'same';
}

interface AlumniClub {
  rank: number;
  name: string;
  badge: string;
  region: string;
  totalMerit: number;
  avgRating: number;
  memberCount: number;
  championships: number;
  winRate: string;
  starPlayer: string;
}

// 三界榜数据（遵循 ~/Downloads/guandan-sanjie-scores.md）
const SANJIE_PLAYERS: SanjiePlayer[] = [
  // 天榜 (功勋 500+)
  {
    rank: 1,
    name: 'Michael HUO (霍老师)',
    avatar: '👑',
    affiliation: '硅谷极客俱乐部 / 浙江大学北美校友会',
    realm: 'tian',
    title: '不朽传说',
    merit: 1280,
    rating: 2480,
    winRate: '78.5%',
    bombCount: 842,
    trophies: ['2026北美高校掼蛋总决赛双人冠军', '复式冠军', '首届硅谷AIGC擂台特等奖'],
    shortcutBadge: '双料总决赛冠军 · 天榜飞升',
    trend: 'up',
  },
  {
    rank: 2,
    name: '陈会长 (President Chen)',
    avatar: '🎖️',
    affiliation: '中山大学美西校友会 (博学审问队)',
    realm: 'tian',
    title: '不朽传说',
    merit: 1210,
    rating: 2420,
    winRate: '75.2%',
    bombCount: 789,
    trophies: ['2025中大校友杯冠军', '北美四人团体赛冠军'],
    shortcutBadge: '大满贯冠军 · 宝典顾问',
    trend: 'same',
  },
  {
    rank: 3,
    name: '张师兄 (Brother Zhang)',
    avatar: '⚡',
    affiliation: '清华大学北美校友会 (紫荆战队)',
    realm: 'tian',
    title: '过A仙班',
    merit: 960,
    rating: 2350,
    winRate: '72.8%',
    bombCount: 654,
    trophies: ['2025常青藤联盟对抗赛亚军', '华体会双人金奖'],
    shortcutBadge: '过A仙班入册',
    trend: 'up',
  },
  {
    rank: 4,
    name: '林博士 (Dr. Lin)',
    avatar: '🦉',
    affiliation: '北京大学北加校友会 (未名湖畔队)',
    realm: 'tian',
    title: '过A仙班',
    merit: 920,
    rating: 2310,
    winRate: '71.4%',
    bombCount: 612,
    trophies: ['2024北美名校掼蛋挑战赛优胜', '五十定律速算冠军'],
    trend: 'same',
  },
  {
    rank: 5,
    name: '王队长 (Captain Wang)',
    avatar: '🦅',
    affiliation: '华中科技大学硅谷校友会 (喻家山先锋)',
    realm: 'tian',
    title: '天王座',
    merit: 780,
    rating: 2240,
    winRate: '69.8%',
    bombCount: 520,
    trophies: ['跨州巡回赛团体季军', '六人团战大师奖'],
    trend: 'up',
  },
  {
    rank: 6,
    name: '周师姐 (Sister Zhou)',
    avatar: '🌟',
    affiliation: '上海交通大学北美校友会 (饮水思源队)',
    realm: 'tian',
    title: '天王座',
    merit: 730,
    rating: 2180,
    winRate: '68.5%',
    bombCount: 490,
    trophies: ['2025女高智汇杯冠军'],
    shortcutBadge: '官方女双冠军',
    trend: 'down',
  },
  {
    rank: 7,
    name: '李学长 (Alumnus Li)',
    avatar: '🛡️',
    affiliation: '复旦大学北加校友会 (日月光华队)',
    realm: 'tian',
    title: '天榜飞升',
    merit: 560,
    rating: 2110,
    winRate: '66.2%',
    bombCount: 415,
    trophies: ['2025高校杯八强'],
    trend: 'up',
  },
  {
    rank: 8,
    name: '郑大师 (Master Zheng)',
    avatar: '🎯',
    affiliation: '南京大学大旧金山校友会 (大哉一诚队)',
    realm: 'tian',
    title: '天榜飞升',
    merit: 520,
    rating: 2080,
    winRate: '65.0%',
    bombCount: 395,
    trophies: ['换位复赛逆转特别奖'],
    trend: 'same',
  },

  // 地榜 (功勋 120-499)
  {
    rank: 9,
    name: '赵工程师 (Engineer Zhao)',
    avatar: '🛠️',
    affiliation: '浙江大学北美校友会',
    realm: 'di',
    title: '宗师座',
    merit: 420,
    rating: 2010,
    winRate: '63.5%',
    bombCount: 340,
    trophies: ['2025秋季擂台赛四强'],
    trend: 'up',
  },
  {
    rank: 10,
    name: '孙研究员 (Dr. Sun)',
    avatar: '🔬',
    affiliation: '中山大学美西校友会',
    realm: 'di',
    title: '宗师座',
    merit: 380,
    rating: 1980,
    winRate: '62.0%',
    bombCount: 310,
    trophies: ['官方双人殿军'],
    shortcutBadge: '官方殿军捷径',
    trend: 'same',
  },
  {
    rank: 11,
    name: '钱架构师 (Arch. Qian)',
    avatar: '💡',
    affiliation: '清华大学北美校友会',
    realm: 'di',
    title: '金阙精英',
    merit: 290,
    rating: 1920,
    winRate: '60.5%',
    bombCount: 260,
    trophies: ['2025友谊赛全胜'],
    trend: 'up',
  },
  {
    rank: 12,
    name: '欧阳快手',
    avatar: '⚡',
    affiliation: '硅谷极客俱乐部',
    realm: 'di',
    title: '星河高手',
    merit: 210,
    rating: 1860,
    winRate: '59.2%',
    bombCount: 210,
    trophies: ['残局速通挑战奖'],
    trend: 'down',
  },
  {
    rank: 13,
    name: '令狐炸皇',
    avatar: '💥',
    affiliation: '华中科技大学硅谷校友会',
    realm: 'di',
    title: '地榜问鼎',
    merit: 140,
    rating: 1810,
    winRate: '57.8%',
    bombCount: 195,
    trophies: ['单局4炸记录'],
    shortcutBadge: '地榜初入册',
    trend: 'up',
  },

  // 人榜 (功勋 8-119)
  {
    rank: 14,
    name: '玉阶先锋 (Pioneer Jade)',
    avatar: '🌱',
    affiliation: '北京大学北加校友会',
    realm: 'ren',
    title: '玉阶牌手',
    merit: 90,
    rating: 1720,
    winRate: '55.4%',
    bombCount: 140,
    trophies: ['高校联赛初赛出线'],
    trend: 'up',
  },
  {
    rank: 15,
    name: '青衫行者',
    avatar: '🚶',
    affiliation: '上海交通大学校友会',
    realm: 'ren',
    title: '青衫新锐',
    merit: 50,
    rating: 1650,
    winRate: '53.1%',
    bombCount: 98,
    trophies: ['额定联机满8局认证'],
    trend: 'same',
  },
  {
    rank: 16,
    name: '初叩学徒 (Apprentice)',
    avatar: '📖',
    affiliation: '北美高校掼蛋爱好者联盟',
    realm: 'ren',
    title: '初叩牌门',
    merit: 16,
    rating: 1550,
    winRate: '50.0%',
    bombCount: 32,
    trophies: ['新手通关认证'],
    trend: 'up',
  },
];

// 高校联盟战队俱乐部排行榜
const ALUMNI_CLUBS: AlumniClub[] = [
  {
    rank: 1,
    name: '浙江大学北美校友会 · 求是鹰之队',
    badge: '🦅',
    region: '北美全境 / 硅谷',
    totalMerit: 3450,
    avgRating: 2240,
    memberCount: 86,
    championships: 5,
    winRate: '73.2%',
    starPlayer: 'Michael HUO (霍老师)',
  },
  {
    rank: 2,
    name: '中山大学美西校友会 · 博学审问队',
    badge: '🏛️',
    region: '北加湾区 / 洛杉矶',
    totalMerit: 3120,
    avgRating: 2190,
    memberCount: 72,
    championships: 4,
    winRate: '71.5%',
    starPlayer: '陈会长 (President Chen)',
  },
  {
    rank: 3,
    name: '清华大学北美校友会 · 紫荆智战队',
    badge: '🟣',
    region: '波士顿 / 硅谷 / 纽约',
    totalMerit: 2840,
    avgRating: 2150,
    memberCount: 68,
    championships: 3,
    winRate: '69.8%',
    starPlayer: '张师兄 (Brother Zhang)',
  },
  {
    rank: 4,
    name: '北京大学北加校友会 · 未名博雅队',
    badge: '🔴',
    region: '旧金山湾区',
    totalMerit: 2560,
    avgRating: 2090,
    memberCount: 59,
    championships: 2,
    winRate: '67.4%',
    starPlayer: '林博士 (Dr. Lin)',
  },
  {
    rank: 5,
    name: '华中科技大学硅谷校友会 · 喻家山先锋',
    badge: '⚙️',
    region: '硅谷 / 西雅图',
    totalMerit: 2310,
    avgRating: 2040,
    memberCount: 54,
    championships: 2,
    winRate: '66.1%',
    starPlayer: '王队长 (Captain Wang)',
  },
  {
    rank: 6,
    name: '上海交通大学北美校友会 · 饮水思源队',
    badge: '⚓',
    region: '全美各地',
    totalMerit: 2180,
    avgRating: 1980,
    memberCount: 48,
    championships: 1,
    winRate: '64.5%',
    starPlayer: '周师姐 (Sister Zhou)',
  },
  {
    rank: 7,
    name: '复旦大学北加校友会 · 日月光华队',
    badge: '✨',
    region: '旧金山湾区',
    totalMerit: 1940,
    avgRating: 1940,
    memberCount: 42,
    championships: 1,
    winRate: '63.0%',
    starPlayer: '李学长 (Alumnus Li)',
  },
  {
    rank: 8,
    name: '硅谷极客创新俱乐部 · AIGC 战队',
    badge: '🚀',
    region: '硅谷 / 南湾',
    totalMerit: 1880,
    avgRating: 1990,
    memberCount: 35,
    championships: 2,
    winRate: '68.9%',
    starPlayer: '欧阳快手',
  },
];

// 三界实时动态
const LIVE_FEED = [
  { id: 1, time: '10分钟前', text: '🏆 Michael HUO 在对决中打出【四大天王天王炸】，问鼎 Rating 达到 2480！' },
  { id: 2, time: '25分钟前', text: '🏛️ 中山大学美西校友会总功勋突破 3100 分，紧咬浙大求是鹰之队！' },
  { id: 3, time: '1小时前', text: '🎖️ 恭喜【令狐炸皇】在额定真人局连克两强，正式荣登【地榜问鼎】席位！' },
  { id: 4, time: '2小时前', text: '🔄 选手在【换位复赛】中以落后两级逆转翻盘，斩获甲案加分 +15！' },
  { id: 5, time: '3小时前', text: '📖 新选手研读《中山大学校友会18条实战宝典》满分，完成人榜认证！' },
];

export const HallView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'tian' | 'di' | 'ren' | 'clubs' | 'rules'>('tian');
  const [searchQuery, setSearchQuery] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncDone, setSyncDone] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile>(() => loadUserProfile());
  const { user, isAuthenticated, getIdTokenClaims } = useAuth0();

  // 手动同步玩家云端战绩至天梯
  const handleCloudSync = async () => {
    setSyncing(true);
    try {
      let token = '';
      if (isAuthenticated) {
        const claims = await getIdTokenClaims();
        token = claims?.__raw || '';
      }
      const res = await syncProfileToCloud(userProfile, token, user?.sub);
      if (res.success && res.profile) {
        setUserProfile(res.profile);
      }
      setSyncDone(true);
      setTimeout(() => setSyncDone(false), 3000);
    } catch (e) {
      console.error('Hall sync error:', e);
    } finally {
      setSyncing(false);
    }
  };

  // 根据当前 activeTab 过滤榜单
  const filteredPlayers = SANJIE_PLAYERS.filter((p) => {
    const matchRealm = activeTab === 'rules' || activeTab === 'clubs' ? true : p.realm === activeTab;
    const matchSearch =
      searchQuery === '' ||
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.affiliation.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.title.includes(searchQuery);
    return matchRealm && matchSearch;
  });

  return (
    <div className="h-full w-full flex flex-col bg-slate-950 text-slate-100 overflow-hidden font-sans">
      {/* Top Banner Header */}
      <div className="shrink-0 bg-gradient-to-r from-slate-950 via-amber-950/40 to-slate-950 border-b border-amber-500/30 p-2 sm:p-3">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-2xl bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20 shrink-0 border border-amber-300/40">
              <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-slate-950" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base sm:text-lg font-black tracking-tight text-white flex items-center gap-1.5">
                  <span>北美高校联盟掼蛋三界榜</span>
                  <span className="text-[10px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded font-black border border-amber-500/40 uppercase">
                    官方功勋天梯
                  </span>
                </h1>
              </div>
              <p className="text-[10px] sm:text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                <span>甲案赛事功勋 (Merit) 只进三界</span>
                <span className="text-slate-600">·</span>
                <span>乙案问鼎天梯 (Rating 1500+)</span>
                <span className="text-slate-600">·</span>
                <span className="text-amber-400/90 font-medium">人机修炼不进三界</span>
              </p>
            </div>
          </div>

          {/* Quick Search & Rules CTA */}
          <div className="flex items-center gap-2 w-full md:w-auto">
            <div className="relative flex-1 md:w-56">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索选手、高校战队或头衔..."
                className="w-full bg-slate-900 border border-slate-700/80 rounded-xl pl-8 pr-2.5 py-1 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-amber-400"
              />
            </div>
            <button
              onClick={() => setActiveTab('rules')}
              className={`px-2.5 py-1 rounded-xl text-xs font-bold flex items-center gap-1 transition-all shrink-0 ${
                activeTab === 'rules'
                  ? 'bg-amber-500 text-slate-950 shadow'
                  : 'bg-slate-900 border border-slate-700 text-slate-300 hover:text-white'
              }`}
            >
              <Scroll className="w-3.5 h-3.5" />
              <span>计分规则</span>
            </button>
          </div>
        </div>
      </div>

      {/* Pinned Current User Standing Card */}
      <div className="shrink-0 bg-slate-900/90 border-b border-slate-800 px-3 py-2 shadow-inner">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <div className="w-8 h-8 rounded-full bg-amber-500/20 border border-amber-400/50 flex items-center justify-center text-sm font-bold text-amber-300 shrink-0">
              {user?.picture ? (
                <img src={user.picture} alt="avatar" className="w-full h-full rounded-full object-cover" />
              ) : (
                userProfile.nickname.slice(0, 1)
              )}
            </div>
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs font-black text-white truncate max-w-[120px]">
                  {userProfile.nickname}
                </span>
                <span className="text-[9px] bg-slate-800 text-amber-300 border border-amber-500/30 px-1 py-0.2 rounded font-bold">
                  {userProfile.rankTier === 'grandmaster'
                    ? '传奇特级大师'
                    : userProfile.rankTier === 'master'
                    ? '省级国手'
                    : userProfile.rankTier === 'elite'
                    ? '联盟精英'
                    : '进阶选手'}
                </span>
                <span className="text-[9px] text-slate-400 font-mono">
                  问鼎 Rating: <strong className="text-cyan-400">1580</strong>
                </span>
                <span className="text-[9px] text-slate-400 font-mono">
                  甲案功勋: <strong className="text-amber-400">32</strong> (人榜青衫)
                </span>
              </div>
              <div className="flex items-center gap-2 text-[9px] text-slate-400 mt-0.5">
                <span>总场次: <strong className="text-slate-200">{userProfile.stats.totalGames}</strong></span>
                <span>头游率: <strong className="text-emerald-400">
                  {userProfile.stats.totalGames > 0
                    ? `${Math.round((userProfile.stats.topRankCount / userProfile.stats.totalGames) * 100)}%`
                    : '0%'}
                </strong></span>
                <span>打出炸弹: <strong className="text-rose-400">{userProfile.stats.bombsPlayed}</strong></span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <button
              onClick={handleCloudSync}
              disabled={syncing}
              className="flex items-center gap-1 px-2.5 py-1 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold border border-slate-700 transition-all active:scale-95 disabled:opacity-50"
              title="将本地战绩同步至北美高校联盟天梯"
            >
              {syncDone ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-300">天梯已同步</span>
                </>
              ) : (
                <>
                  <Cloud className={`w-3.5 h-3.5 ${syncing ? 'animate-spin text-amber-400' : 'text-sky-400'}`} />
                  <span>{syncing ? '同步中...' : '同步天梯战绩'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Realm Selection Tabs */}
      <div className="shrink-0 bg-slate-950/80 px-3 pt-2 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex items-center space-x-1 sm:space-x-2 overflow-x-auto scrollbar-none">
          <button
            onClick={() => setActiveTab('tian')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t-xl text-xs font-black transition-all border-t border-x ${
              activeTab === 'tian'
                ? 'bg-gradient-to-b from-amber-500/20 to-transparent border-amber-500/50 text-amber-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Crown className="w-3.5 h-3.5 text-amber-400" />
            <span>天榜 · 飞升仙班</span>
            <span className="text-[9px] bg-amber-500/20 text-amber-300 px-1 py-0.2 rounded font-mono">
              功勋 500+
            </span>
          </button>

          <button
            onClick={() => setActiveTab('di')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t-xl text-xs font-black transition-all border-t border-x ${
              activeTab === 'di'
                ? 'bg-gradient-to-b from-sky-500/20 to-transparent border-sky-500/50 text-sky-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Medal className="w-3.5 h-3.5 text-sky-400" />
            <span>地榜 · 问鼎宗师</span>
            <span className="text-[9px] bg-sky-500/20 text-sky-300 px-1 py-0.2 rounded font-mono">
              功勋 120–499
            </span>
          </button>

          <button
            onClick={() => setActiveTab('ren')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t-xl text-xs font-black transition-all border-t border-x ${
              activeTab === 'ren'
                ? 'bg-gradient-to-b from-emerald-500/20 to-transparent border-emerald-500/50 text-emerald-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Award className="w-3.5 h-3.5 text-emerald-400" />
            <span>人榜 · 青衫新锐</span>
            <span className="text-[9px] bg-emerald-500/20 text-emerald-300 px-1 py-0.2 rounded font-mono">
              功勋 8–119
            </span>
          </button>

          <button
            onClick={() => setActiveTab('clubs')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t-xl text-xs font-black transition-all border-t border-x ${
              activeTab === 'clubs'
                ? 'bg-gradient-to-b from-purple-500/20 to-transparent border-purple-500/50 text-purple-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <GraduationCap className="w-3.5 h-3.5 text-purple-400" />
            <span>高校联盟战队榜</span>
            <span className="text-[9px] bg-purple-500/20 text-purple-300 px-1 py-0.2 rounded font-mono">
              校友会
            </span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-h-0 overflow-y-auto p-2 sm:p-4">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4">
          
          {/* Left 2 Columns: Selected Tab Content */}
          <div className="lg:col-span-2 space-y-3">
            
            {/* Rules Tab */}
            {activeTab === 'rules' && (
              <div className="bg-slate-900/90 border border-amber-500/30 rounded-2xl p-4 sm:p-5 space-y-4">
                <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
                  <Scroll className="w-5 h-5 text-amber-400" />
                  <div>
                    <h2 className="text-sm font-black text-amber-300">北美高校联盟掼蛋三界榜 · 官方计分大纲</h2>
                    <p className="text-[11px] text-slate-400">两种成绩互不兑换 · 官方功勋只进三界 · 站内修炼管顶栏</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="bg-slate-950 p-3 rounded-xl border border-slate-800 space-y-2">
                    <div className="flex items-center gap-1.5 text-amber-300 font-bold">
                      <Crown className="w-4 h-4" />
                      <span>甲案：官方赛事功勋 Merit</span>
                    </div>
                    <ul className="space-y-1 text-slate-300 text-[11px] list-disc list-inside">
                      <li>只收联盟主办或经官方认证合作赛 (sanctioned=true)</li>
                      <li>个人/双人冠军：<strong>100 功勋</strong>；亚军：<strong>60</strong>；季军：<strong>40</strong></li>
                      <li>团体赛冠军每人：<strong>70 功勋</strong>；亚军：<strong>40</strong></li>
                      <li>加分项：复式/换位复赛冠军 +15；跨州完赛 +5；卫冕冠军各 +10</li>
                      <li>功勋只升不降，用于确定人榜、地榜、天榜三界席位</li>
                    </ul>
                  </div>

                  <div className="bg-slate-950 p-3 rounded-xl border border-slate-800 space-y-2">
                    <div className="flex items-center gap-1.5 text-cyan-300 font-bold">
                      <TrendingUp className="w-4 h-4" />
                      <span>乙案：问鼎 Rating (额定真人天梯)</span>
                    </div>
                    <ul className="space-y-1 text-slate-300 text-[11px] list-disc list-inside">
                      <li>起始 1500 分，采用标准双人 Elo 模型 (K=24)</li>
                      <li>双上 S=1.00，单上且升级≥2 S=0.85，单上 S=0.70</li>
                      <li>四副打底或过A结束，托管≤40%方计额定真人分</li>
                      <li>天梯档位：试剑 (&lt;1500)、问鼎 (1500)、青云 (1650)、金阙 (1800)、飞升 (1900)、天王 (2100)、过A (2300)</li>
                      <li>满 30 局且 Rating≥1900 可获天榜展示捷径资格</li>
                    </ul>
                  </div>
                </div>

                {/* Realm Threshold Table */}
                <div className="overflow-x-auto rounded-xl border border-slate-800">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-950 text-slate-400 font-bold border-b border-slate-800">
                      <tr>
                        <th className="p-2.5">三界阶梯</th>
                        <th className="p-2.5">席位软顶</th>
                        <th className="p-2.5">等级称号</th>
                        <th className="p-2.5">所需功勋 (Merit)</th>
                        <th className="p-2.5">快捷入册特权</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/60 font-medium text-slate-300 text-[11px]">
                      <tr className="bg-amber-500/5">
                        <td className="p-2.5 font-bold text-amber-300">天榜 (飞升仙班)</td>
                        <td className="p-2.5 font-mono text-amber-400">36 席</td>
                        <td className="p-2.5">不朽传说 / 过A仙班 / 天王座 / 天榜飞升</td>
                        <td className="p-2.5 font-mono">500 – 1200+</td>
                        <td className="p-2.5 text-amber-300">联盟官方个人/双人冠军直接展示</td>
                      </tr>
                      <tr className="bg-sky-500/5">
                        <td className="p-2.5 font-bold text-sky-300">地榜 (问鼎宗师)</td>
                        <td className="p-2.5 font-mono text-sky-400">108 席</td>
                        <td className="p-2.5">宗师座 / 金阙精英 / 星河高手 / 地榜问鼎</td>
                        <td className="p-2.5 font-mono">120 – 499</td>
                        <td className="p-2.5 text-sky-300">官方亚、季或优胜直接展示</td>
                      </tr>
                      <tr className="bg-emerald-500/5">
                        <td className="p-2.5 font-bold text-emerald-300">人榜 (青衫新锐)</td>
                        <td className="p-2.5 font-mono text-emerald-400">不限</td>
                        <td className="p-2.5">玉阶牌手 / 青衫新锐 / 初叩牌门</td>
                        <td className="p-2.5 font-mono">8 – 119 (未满8不上榜)</td>
                        <td className="p-2.5 text-emerald-300">已注册且官方完赛1次或满8局</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Clubs Tab */}
            {activeTab === 'clubs' && (
              <div className="space-y-2">
                <div className="bg-slate-900/60 border border-slate-800 p-2.5 rounded-xl flex items-center justify-between text-xs">
                  <span className="text-slate-300">北美高校校友会战队官方锦标赛总榜</span>
                  <span className="text-purple-400 font-mono text-[11px]">8 所顶尖高校战队在册</span>
                </div>

                {ALUMNI_CLUBS.map((club) => (
                  <div
                    key={club.rank}
                    className="bg-slate-900/80 hover:bg-slate-850 border border-slate-800/80 hover:border-purple-500/40 rounded-xl p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5 transition-all shadow"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-7 h-7 rounded-lg flex items-center justify-center font-black text-xs shrink-0 ${
                          club.rank === 1
                            ? 'bg-amber-500 text-slate-950 font-black'
                            : club.rank === 2
                            ? 'bg-slate-300 text-slate-950'
                            : club.rank === 3
                            ? 'bg-amber-700 text-white'
                            : 'bg-slate-800 text-slate-400'
                        }`}
                      >
                        {club.rank}
                      </div>
                      <span className="text-xl shrink-0">{club.badge}</span>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h3 className="text-xs sm:text-sm font-bold text-white">{club.name}</h3>
                          <span className="text-[9px] bg-slate-800 text-slate-400 px-1 rounded">
                            {club.region}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-0.5">
                          <span>战队总功勋: <strong className="text-amber-400 font-mono">{club.totalMerit}</strong></span>
                          <span>·</span>
                          <span>平均问鼎分: <strong className="text-cyan-400 font-mono">{club.avgRating}</strong></span>
                          <span>·</span>
                          <span>主力代表: <strong className="text-slate-200">{club.starPlayer}</strong></span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 self-end sm:self-center shrink-0">
                      <div className="text-right">
                        <div className="text-xs font-black text-emerald-400">{club.winRate} 胜率</div>
                        <div className="text-[9px] text-slate-500">{club.championships} 次夺冠 · {club.memberCount} 位战将</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Players Table (Tian / Di / Ren) */}
            {(activeTab === 'tian' || activeTab === 'di' || activeTab === 'ren') && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[11px] text-slate-400 px-1">
                  <span>
                    当前榜单：
                    {activeTab === 'tian' && <strong className="text-amber-300">天榜 (飞升仙班 · 功勋 500+)</strong>}
                    {activeTab === 'di' && <strong className="text-sky-300">地榜 (问鼎宗师 · 功勋 120-499)</strong>}
                    {activeTab === 'ren' && <strong className="text-emerald-300">人榜 (青衫新锐 · 功勋 8-119)</strong>}
                  </span>
                  <span>共 {filteredPlayers.length} 位牌仙/高手在册</span>
                </div>

                {filteredPlayers.length === 0 ? (
                  <div className="p-8 text-center bg-slate-900/50 border border-slate-800 rounded-2xl">
                    <p className="text-xs text-slate-400">未找到符合搜索条件的选手</p>
                  </div>
                ) : (
                  filteredPlayers.map((player) => (
                    <div
                      key={player.rank}
                      className={`p-3 rounded-2xl border transition-all flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-md ${
                        player.realm === 'tian'
                          ? 'bg-gradient-to-r from-slate-900/90 via-amber-950/20 to-slate-900/90 border-amber-500/40 hover:border-amber-400'
                          : player.realm === 'di'
                          ? 'bg-gradient-to-r from-slate-900/90 via-sky-950/20 to-slate-900/90 border-sky-500/40 hover:border-sky-400'
                          : 'bg-slate-900/90 border-slate-800 hover:border-emerald-500/40'
                      }`}
                    >
                      {/* Left: Rank & Avatar & Info */}
                      <div className="flex items-center gap-3">
                        {/* Rank Badge */}
                        <div
                          className={`w-7 h-7 rounded-xl flex items-center justify-center font-black text-xs shrink-0 shadow ${
                            player.rank === 1
                              ? 'bg-amber-400 text-slate-950 ring-2 ring-amber-300/60'
                              : player.rank === 2
                              ? 'bg-slate-300 text-slate-950'
                              : player.rank === 3
                              ? 'bg-amber-600 text-white'
                              : 'bg-slate-800 text-slate-300'
                          }`}
                        >
                          {player.rank}
                        </div>

                        {/* Player Avatar */}
                        <div className="w-9 h-9 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-base shrink-0">
                          {player.avatar}
                        </div>

                        <div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <h3 className="text-xs sm:text-sm font-black text-white">{player.name}</h3>
                            <span
                              className={`text-[9px] px-1.5 py-0.2 rounded font-black border uppercase ${
                                player.realm === 'tian'
                                  ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                                  : player.realm === 'di'
                                  ? 'bg-sky-500/20 text-sky-300 border-sky-500/40'
                                  : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                              }`}
                            >
                              {player.title}
                            </span>
                            {player.shortcutBadge && (
                              <span className="text-[8px] bg-purple-500/20 text-purple-300 border border-purple-500/40 px-1 py-0.2 rounded">
                                {player.shortcutBadge}
                              </span>
                            )}
                          </div>

                          <div className="text-[10px] text-slate-400 flex items-center gap-2 mt-0.5">
                            <span className="text-slate-300">{player.affiliation}</span>
                            <span>·</span>
                            <span>胜率: <strong className="text-emerald-400">{player.winRate}</strong></span>
                            <span>·</span>
                            <span>炸弹: <strong className="text-rose-400">{player.bombCount}</strong></span>
                          </div>
                        </div>
                      </div>

                      {/* Right: Merit & Elo Rating Scores */}
                      <div className="flex items-center gap-4 self-end sm:self-center shrink-0">
                        <div className="text-right">
                          <div className="text-[9px] text-slate-500 uppercase tracking-wider font-bold">
                            甲案功勋 Merit
                          </div>
                          <div className="text-sm font-black text-amber-400 font-mono">
                            {player.merit} <span className="text-[10px] font-normal text-amber-500/80">分</span>
                          </div>
                        </div>

                        <div className="text-right border-l border-slate-800 pl-3">
                          <div className="text-[9px] text-slate-500 uppercase tracking-wider font-bold">
                            问鼎 Rating (Elo)
                          </div>
                          <div className="text-sm font-black text-cyan-400 font-mono">
                            {player.rating}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Right Column: 5D Radar & Live Feed */}
          <div className="space-y-4">
            
            {/* 5D Combat Power Radar Box */}
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-4 shadow-xl space-y-3">
              <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                <div className="flex items-center gap-1.5">
                  <Zap className="w-4 h-4 text-amber-400" />
                  <h3 className="text-xs font-black text-white">全维战力评级 (5D Combat Power)</h3>
                </div>
                <span className="text-[10px] text-slate-400 font-mono">基于实战算法</span>
              </div>

              {/* 5-Dimension Bars */}
              <div className="space-y-2 text-xs">
                <div>
                  <div className="flex justify-between text-[10px] mb-1">
                    <span className="text-slate-300">控火炸弹力 (Fire Control)</span>
                    <span className="text-rose-400 font-bold font-mono">96 / 100</span>
                  </div>
                  <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                    <div className="bg-gradient-to-r from-orange-500 to-rose-500 h-full rounded-full" style={{ width: '96%' }} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-[10px] mb-1">
                    <span className="text-slate-300">记牌神算力 (Fifty Laws Tracker)</span>
                    <span className="text-cyan-400 font-bold font-mono">92 / 100</span>
                  </div>
                  <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                    <div className="bg-gradient-to-r from-cyan-500 to-blue-500 h-full rounded-full" style={{ width: '92%' }} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-[10px] mb-1">
                    <span className="text-slate-300">默契配合力 (Partner Synergy)</span>
                    <span className="text-amber-400 font-bold font-mono">94 / 100</span>
                  </div>
                  <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                    <div className="bg-gradient-to-r from-amber-500 to-yellow-400 h-full rounded-full" style={{ width: '94%' }} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-[10px] mb-1">
                    <span className="text-slate-300">残局逆转力 (Endgame Reversal)</span>
                    <span className="text-purple-400 font-bold font-mono">88 / 100</span>
                  </div>
                  <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                    <div className="bg-gradient-to-r from-indigo-500 to-purple-500 h-full rounded-full" style={{ width: '88%' }} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-[10px] mb-1">
                    <span className="text-slate-300">牌型规划力 (Hand Planning)</span>
                    <span className="text-emerald-400 font-bold font-mono">95 / 100</span>
                  </div>
                  <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                    <div className="bg-gradient-to-r from-teal-500 to-emerald-500 h-full rounded-full" style={{ width: '95%' }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Live Feed News */}
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-4 shadow-xl space-y-3">
              <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                <div className="flex items-center gap-1.5">
                  <Flame className="w-4 h-4 text-rose-400" />
                  <h3 className="text-xs font-black text-white">三界荣誉战报动态</h3>
                </div>
                <span className="text-[9px] text-emerald-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  实时天梯
                </span>
              </div>

              <div className="space-y-2.5">
                {LIVE_FEED.map((item) => (
                  <div key={item.id} className="text-[11px] bg-slate-950 p-2.5 rounded-xl border border-slate-800/80">
                    <div className="text-[9px] text-slate-500 font-mono mb-1">{item.time}</div>
                    <p className="text-slate-300 leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer Note from ~/Downloads/guandan-sanjie-scores.md */}
            <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 text-[10px] text-slate-400 leading-relaxed">
              <p className="text-amber-400 font-bold mb-1">📌 联盟立榜公约：</p>
              三界榜为北美高校联盟掼蛋赛事立榜以来官方功勋榜。人机练习与残局只计修炼（EXP），不计入三界排序。额定真人局满额方计问鼎 Rating。
            </div>

          </div>

        </div>
      </div>
    </div>
  );
};
